import type { CorrectionLetterResponse, ReasonSelection } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchCorrectionLetterApi } from "../../../../../api/client";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { correctionLetterKeys } from "./correctionLetterKeys";

/**
 * Mutation hook for saving Correction Letter reason selections against a deal.
 * Backed by `WorkbenchCorrectionLetterApi.saveCorrectionLetter` (POST), which
 * forwards the desired `{ code, selected }` state for each reason to
 * recap-service and returns the refreshed CorrectionLetterResponse. A
 * per-attempt `Idempotency-Key` dedups accidental double-submits. On success the
 * response is written straight into the GET cache, so no refetch is needed.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveCorrectionLetter(approvalId: string) {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async (reasons: ReasonSelection[]): Promise<CorrectionLetterResponse | undefined> => {
      if (shouldForceExit) {
        return undefined;
      }
      const idempotencyKey = globalThis.crypto?.randomUUID?.();
      const response = await workbenchCorrectionLetterApi.saveCorrectionLetter(approvalId, { reasons }, idempotencyKey);
      return response.data;
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(correctionLetterKeys.detail({ approvalId }), data);
      }
    },
  });
}
